// ============================================================================
// BOOKING HOOKS
// ============================================================================
// React hooks for Booking API operations

import { useState, useEffect, useCallback } from 'react';
import { bookingService } from 'api/services/bookingService';
import type {
  Booking,
  BookingFull,
  CreateBookingRequest,
  UpdateBookingRequest,
  BookingSearchRequest,
} from 'api/types/booking.types';
import type { ID } from 'api/types';

/**
 * Hook to fetch all bookings (paginated)
 */
export const useBookings = (params?: BookingSearchRequest) => {
  const [data, setData] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    hasNextPage: false,
    hasPreviousPage: false,
  });

  const paramsKey = JSON.stringify(params);

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await bookingService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  return {
    data,
    loading,
    error,
    pagination,
    refetch: fetchBookings,
  };
};

/**
 * Hook to fetch a single booking by ID
 */
export const useBooking = (id: ID) => {
  const [data, setData] = useState<Booking | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchBooking = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await bookingService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  return {
    data,
    loading,
    error,
    refetch: fetchBooking,
  };
};

/**
 * Hook to fetch full booking (header + details + guests)
 */
export const useFullBooking = (id: ID) => {
  const [data, setData] = useState<BookingFull | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchFullBooking = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await bookingService.getFullBooking(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchFullBooking();
  }, [fetchFullBooking]);

  return {
    data,
    loading,
    error,
    refetch: fetchFullBooking,
  };
};

/**
 * Hook to create a new booking
 */
export const useCreateBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createBooking = async (
    data: CreateBookingRequest,
    options?: {
      onSuccess?: (booking: Booking) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await bookingService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    createBooking,
    loading,
    error,
    success,
  };
};

/**
 * Hook to update a booking
 */
export const useUpdateBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateBooking = async (
    id: ID,
    data: UpdateBookingRequest,
    options?: {
      onSuccess?: (booking: Booking) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await bookingService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    updateBooking,
    loading,
    error,
    success,
  };
};

/**
 * Hook to delete a booking
 */
export const useDeleteBooking = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteBooking = async (
    id: ID,
    rowVersion: number,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await bookingService.delete(id, rowVersion);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      options?.onError?.(error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    deleteBooking,
    loading,
    error,
    success,
  };
};
